/* eslint-disable complexity */
/* eslint-disable max-lines */
import React, { useCallback, useContext, useMemo } from 'react';
import { LeftNavContext } from '../../LeftNavigationContext.js';
import { LEFT_NAVIGATION_DATA_TESTID } from '../../constants/index.js';
import { useKeyboardNavigation } from '../../hooks/index.js';
import type { DSLeftNavigationT } from '../../react-desc-prop-types.js';
import { StyledBorderBottom, StyledCollapsedContainer, StyledItem } from './styled.js';
import { usePropsWithDefaults } from './usePropsWithDefaults.js';

export const ItemRenderer: React.ComponentType<DSLeftNavigationT.ItemRendererT> = (props) => {
  const propsWithDefault = usePropsWithDefaults(props);
  const {
    item,
    item: {
      dsId,
      itemOpts: { selectable = false, focuseable, indent, ariaLabel, ariaRole, ariaExpanded, ariaDescribedBy },
      CollapsedComponent,
    },
    subitems,
    minHeight,
    paddingTop,
    onClick,
    shadowStyle,
    hasBorderBottom,
    borderBottomMr,
    children,
  } = propsWithDefault;

  const ctx = useContext(LeftNavContext);
  const {
    leftNavProps,
    leftNavProps: { onItemClick, openedItem },
    expandedForAnimation,
    selectedParent,
    selectedItem,
    setSelectedItem,
    setFocusedItem,
    visibleItemsRefs,
  } = ctx;
  const getOwnerProps = React.useCallback(() => leftNavProps, [leftNavProps]);
  const getOwnerPropsArguments = React.useCallback(() => ({}), []);

  const opened = dsId === openedItem;

  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (onClick) onClick(); // used by uncontrolled drilldowns
      if (onItemClick) onItemClick(item, e);
      if (selectable) setSelectedItem(item.dsId as string);
    },
    [onItemClick, item, setSelectedItem, selectable, onClick],
  );

  const handleFocus = useCallback(() => {
    if (focuseable) setFocusedItem(dsId as string);
  }, [dsId, setFocusedItem, focuseable]);

  const onKeyDown = useKeyboardNavigation({
    item,
    onClick: handleOnClick,
  });

  const cols = useMemo(() => (expandedForAnimation ? ['auto', '1fr', 'auto'] : ['38px']), [expandedForAnimation]);
  const isSelected = useMemo(() => selectedItem === dsId, [selectedItem, dsId]);

  if (!expandedForAnimation && !CollapsedComponent) {
    return null;
  }

  const isSelectedParent = selectedParent === dsId;
  const visibleItemRef = dsId ? visibleItemsRefs.current[dsId] : undefined;
  return (
    <>
      <StyledItem
        innerRef={visibleItemRef}
        pt={paddingTop}
        pl={indent === undefined ? 'xxxs' : `${indent}px`}
        minHeight={minHeight}
        shadowStyle={shadowStyle(opened, item.type)}
        alignItems="flex-start"
        cols={cols}
        onClick={!opened ? handleOnClick : () => null}
        onKeyDown={onKeyDown}
        selectable={selectable}
        selected={isSelected}
        selectedParent={isSelectedParent}
        opened={opened}
        onFocus={handleFocus}
        tabIndex={focuseable ? 0 : undefined}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        role={ariaRole}
        data-selected={isSelected}
        data-selectedparent={isSelectedParent}
        data-opened={opened}
        data-type={item.type}
        aria-label={ariaLabel}
        data-testid={LEFT_NAVIGATION_DATA_TESTID.ITEM} // https://jira.elliemae.io/browse/PUI-15589
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {!expandedForAnimation ? (
          <StyledCollapsedContainer
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
            justifyContent="center"
            data-testid={LEFT_NAVIGATION_DATA_TESTID.COLLAPSED_CONTAINER} // https://jira.elliemae.io/browse/PUI-15589
          >
            {CollapsedComponent && <CollapsedComponent item={item} ctx={ctx} />}
          </StyledCollapsedContainer>
        ) : (
          children
        )}
        {hasBorderBottom && (
          <StyledBorderBottom
            opened={opened}
            borderBottomMr={borderBottomMr}
            data-testid={LEFT_NAVIGATION_DATA_TESTID.ITEM_BORDER_BOTTOM} // https://jira.elliemae.io/browse/PUI-15589
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
          />
        )}
      </StyledItem>
      {subitems}
    </>
  );
};
